import { useState, useEffect } from 'react';
import { useTripBuilder } from '../TripBuilderContext';
import Logo from './Logo';

// Common transferable + airline/hotel programs the user might track
const PROGRAM_CATALOG = [
  { key: 'amex_mr', label: 'Amex Membership Rewards', primary: true },
  { key: 'chase_ur', label: 'Chase Ultimate Rewards', primary: true },
  { key: 'capital_one', label: 'Capital One Venture Miles' },
  { key: 'citi_ty', label: 'Citi ThankYou Points' },
  { key: 'bilt', label: 'Bilt Rewards' },
  { key: 'marriott', label: 'Marriott Bonvoy' },
  { key: 'hyatt', label: 'World of Hyatt' },
  { key: 'hilton', label: 'Hilton Honors' },
  { key: 'ihg', label: 'IHG One Rewards' },
  { key: 'aa', label: 'American AAdvantage' },
  { key: 'united', label: 'United MileagePlus' },
  { key: 'delta', label: 'Delta SkyMiles' },
  { key: 'alaska', label: 'Alaska Mileage Plan' },
  { key: 'aeroplan', label: 'Air Canada Aeroplan' },
  { key: 'avios_ba', label: 'British Airways Avios' },
  { key: 'avios_iberia', label: 'Iberia Avios' },
  { key: 'flying_blue', label: 'Air France/KLM Flying Blue' },
  { key: 'virgin_atlantic', label: 'Virgin Atlantic Flying Club' },
  { key: 'lifemiles', label: 'Avianca LifeMiles' },
  { key: 'ana', label: 'ANA Mileage Club' },
  { key: 'singapore', label: 'Singapore KrisFlyer' },
  { key: 'turkish', label: 'Turkish Miles & Smiles' },
];

const CABIN_OPTIONS = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium', label: 'Premium Economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First' },
];

function programLabel(key) {
  return PROGRAM_CATALOG.find(p => p.key === key)?.label || key;
}

export default function UserSetup({ onClose, forced = false }) {
  const { userConfig, setUserConfig } = useTripBuilder();

  const [names, setNames] = useState(userConfig.travelerNames || '');
  const [travelers, setTravelers] = useState(userConfig.travelers || 2);
  const [cabin, setCabin] = useState(userConfig.cabinClass || 'business');
  const [origin, setOrigin] = useState(userConfig.origin || 'JFK');
  const [balances, setBalances] = useState(() => ({
    amex_mr: userConfig.pointsBalances?.amex_mr ?? 0,
    chase_ur: userConfig.pointsBalances?.chase_ur ?? 0,
    ...userConfig.pointsBalances,
  }));
  const [showAddProgram, setShowAddProgram] = useState(false);
  const [newProgramKey, setNewProgramKey] = useState('');

  // Programs that are currently shown (in order)
  const shownPrograms = Object.keys(balances);
  const addablePrograms = PROGRAM_CATALOG.filter(p => !shownPrograms.includes(p.key));

  // Esc to close (only if not forced)
  useEffect(() => {
    if (forced) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [forced, onClose]);

  const handleBalanceChange = (key, value) => {
    // Strip non-numeric characters, allow empty
    const clean = String(value).replace(/[^0-9]/g, '');
    setBalances(prev => ({ ...prev, [key]: clean === '' ? '' : Number(clean) }));
  };

  const handleAddProgram = () => {
    if (!newProgramKey) return;
    setBalances(prev => ({ ...prev, [newProgramKey]: 0 }));
    setNewProgramKey('');
    setShowAddProgram(false);
  };

  const handleRemoveProgram = (key) => {
    if (key === 'amex_mr' || key === 'chase_ur') return; // keep core programs visible
    setBalances(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = () => {
    // Coerce empty strings to 0 in balances
    const cleanBalances = Object.fromEntries(
      Object.entries(balances).map(([k, v]) => [k, Number(v) || 0])
    );
    setUserConfig({
      travelerNames: names.trim(),
      travelers: Number(travelers) || 1,
      cabinClass: cabin,
      origin: origin.trim().toUpperCase() || 'JFK',
      pointsBalances: cleanBalances,
      configured: true,
    });
    onClose?.();
  };

  const totalPoints = Object.values(balances).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      <div className={`absolute inset-0 ${forced ? 'bg-navy' : 'bg-black/70 backdrop-blur-sm'}`} onClick={forced ? undefined : onClose} />
      <div className="relative bg-navy-light border border-brand-ink/10 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 sm:p-6 space-y-5">
          {/* Header */}
          {forced && (
            <div className="flex justify-center pt-1">
              <Logo height={40} />
            </div>
          )}
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-teal/70 font-bold uppercase tracking-widest mb-1">
                {forced ? 'Welcome to PointsPilot' : 'Trip Settings'}
              </div>
              <h2 className="text-2xl font-extrabold">
                {forced ? "Let's set up your trip" : 'Edit your details'}
              </h2>
              <p className="text-sm text-brand-ink/70 mt-1">
                {forced
                  ? 'Tell us about your trip and points so we can plan around them.'
                  : 'Update your travelers, cabin, and points balances anytime.'}
              </p>
            </div>
            {!forced && (
              <button
                onClick={onClose}
                className="text-brand-ink/65 hover:text-white text-2xl w-9 h-9 flex items-center justify-center rounded-full hover:bg-brand-ink/5 transition flex-shrink-0"
              >
                ×
              </button>
            )}
          </div>

          {/* Trip details */}
          <div className="space-y-3">
            <Field label="Who's going?" hint="e.g. your name(s)">
              <input
                type="text"
                value={names}
                onChange={e => setNames(e.target.value)}
                placeholder="Your name(s)"
                className="w-full bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-brand-ink/40 focus:outline-none focus:border-teal/60"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Travelers">
                <select
                  value={travelers}
                  onChange={e => setTravelers(Number(e.target.value))}
                  className="w-full bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-teal/60"
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                  ))}
                </select>
              </Field>

              <Field label="Cabin class">
                <select
                  value={cabin}
                  onChange={e => setCabin(e.target.value)}
                  className="w-full bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-teal/60"
                >
                  {CABIN_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Home airport" hint="Departure airport code (e.g. JFK, LAX, ORD)">
              <input
                type="text"
                value={origin}
                onChange={e => setOrigin(e.target.value.toUpperCase().slice(0, 4))}
                placeholder="JFK"
                className="w-full bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-brand-ink/40 focus:outline-none focus:border-teal/60 uppercase"
              />
            </Field>
          </div>

          {/* Points balances */}
          <div className="space-y-3 pt-2 border-t border-brand-ink/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-white">Your points balances</div>
                <div className="text-xs text-brand-ink/65 mt-0.5">Add whichever programs you have. Amex MR and Chase UR drive the booking logic.</div>
              </div>
              {totalPoints > 0 && (
                <div className="text-right">
                  <div className="text-xs text-brand-ink/65">Total</div>
                  <div className="text-sm font-bold text-teal">{totalPoints.toLocaleString()}</div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {shownPrograms.map(key => {
                const isCore = key === 'amex_mr' || key === 'chase_ur';
                return (
                  <div key={key} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-brand-ink/75 truncate">{programLabel(key)}{isCore && <span className="text-teal/60 ml-1">●</span>}</div>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={balances[key] === '' ? '' : (balances[key] ?? '').toLocaleString()}
                      onChange={e => handleBalanceChange(key, e.target.value)}
                      placeholder="0"
                      className="w-32 bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2 text-sm text-right text-white placeholder-brand-ink/40 focus:outline-none focus:border-teal/60 tabular-nums"
                    />
                    {!isCore && (
                      <button
                        onClick={() => handleRemoveProgram(key)}
                        className="text-brand-ink/55 hover:text-danger text-sm w-7 h-7 flex items-center justify-center rounded transition"
                        title="Remove"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add program */}
            {addablePrograms.length > 0 && (
              showAddProgram ? (
                <div className="flex items-center gap-2 bg-brand-ink/5 border border-brand-ink/10 rounded-lg p-2">
                  <select
                    value={newProgramKey}
                    onChange={e => setNewProgramKey(e.target.value)}
                    className="flex-1 bg-navy-light border border-brand-ink/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal/60"
                  >
                    <option value="">Pick a program…</option>
                    {addablePrograms.map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddProgram}
                    disabled={!newProgramKey}
                    className="px-3 py-2 bg-teal text-navy font-bold text-xs rounded-lg disabled:opacity-30"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setShowAddProgram(false); setNewProgramKey(''); }}
                    className="px-2 text-xs text-brand-ink/65 hover:text-brand-ink/75"
                  >
                    cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddProgram(true)}
                  className="w-full py-2 text-xs font-medium text-brand-ink/70 hover:text-teal border border-dashed border-brand-ink/10 hover:border-teal/40 rounded-lg transition"
                >
                  + Add another program
                </button>
              )
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {!forced && (
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm text-brand-ink/75 hover:text-white"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-3 bg-teal text-navy font-extrabold rounded-xl text-sm shadow-lg shadow-teal/20 hover:opacity-90 transition"
            >
              {forced ? "Let's plan a trip" : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink/75 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-brand-ink/55 mt-1">{hint}</div>}
    </label>
  );
}
